import React from 'react';
import { Link } from 'react-router-dom';
import { RouterConstant } from '../../../routers/index';
import ChatInputTool from '../../../components/v2/chat-input-tool';
import { flameIcon, Loader } from '../../../assets/images/index';
import StatsCard from '../../../components/v2/stats-card';
import MissionCard from '../../../components/v2/mission-card';
import BotMessage from '../../../components/v2/bot-message';
import UserMessage from '../../../components/v2/user-message';
import { QuickReplyChips } from '../../../components/v2/quick-reply-chips';
import MathJaxForChatPage from '../../../components/common/mathjaxforchatpage';
import { useDashboard } from './hooks/useDashboard';
import { CatalogGrid } from '../../../components/v2/catalog-grid';
import SmartLessonCatalog from '../../../components/v2/smart-lesson-catalog';
import './user-main-dashboard.scss';

export default function UserMainDashboard() {
  const {
    t,
    navigate,
    userInfo,
    currentStreak,
    isAwardedToday,
    loadingGamification,
    progressMissions,
    loadingMissions,
    scrollContentRef,
    chatBottomRef,
    messages,
    isAgentLoading,
    handleSendMessage,
    handleMissionPress,
    openModesModal,
    
    // Returning user states
    returningMetadata,
    loadingMetadata,
    isGreetingTyping,
    showGreeting,
    loadingRetroText,
    showRetroText,
    isAgentLoadingManual,
    retroStep,
    isReturningUser,
    showQuickReplies,
    quickReplyOptions,
    user_attributes,
  } = useDashboard();

  const isChatCentered = true;

  if (loadingMetadata) {
    return (
      <div className="v2-dashboard-layout v2-dashboard-layout--loading">
        <img src={Loader} alt="Loading..." className="v2-dashboard-loader" />
      </div>
    );
  }

  const renderRetroBubble = () => {
    if (!returningMetadata) return null;
    
    const streak = returningMetadata.learning_streak_days || 0;
    const visits = returningMetadata.total_visits || 0;
    const lastLoginRaw = returningMetadata.last_login_date;
    
    let lastLogin = '';
    if (lastLoginRaw) {
      const d = new Date(lastLoginRaw);
      lastLogin = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    }

    const genderVal = user_attributes?.gender;
    const isFemale = genderVal === 2 || genderVal === '2' || genderVal === 'female';
    const isNeutral = genderVal === 3 || genderVal === '3' || genderVal === 'neutral';
    const pronoun = isFemale ? t('v2DashboardPronoun_female') : t('v2DashboardPronoun_male');

    const part1 = streak > 0
      ? t('v2DashboardGreeting_streak_yes_part1', { streak, visits })
      : t('v2DashboardGreeting_streak_no_part1', { visits });

    const clickHereKey = isFemale 
      ? 'v2DashboardGreeting_streak_click_here_female' 
      : (isNeutral ? 'v2DashboardGreeting_streak_click_here_neutral' : 'v2DashboardGreeting_streak_click_here_male');
      
    const part2Key = isFemale 
      ? 'v2DashboardGreeting_streak_part2_female' 
      : (isNeutral ? 'v2DashboardGreeting_streak_part2_neutral' : 'v2DashboardGreeting_streak_part2_male');

    const part2 = t(part2Key, { lastLogin, pronoun });

    return (
      <BotMessage id="dashboard-retro-message" skipAnimation={true}>
        <div className="v2-dashboard-retro-bubble">
          <MathJaxForChatPage content={part1} instant={true} />
          <p>
            {t('v2DashboardGreeting_streak_progress')}
            <Link to={RouterConstant.ROUTERS_PATH.USER_ALL_MISSIONS} className="v2-dashboard-retro-link">
              {t(clickHereKey)}
            </Link>
          </p>
          <MathJaxForChatPage content={part2} instant={true} />
        </div>
      </BotMessage>
    );
  };

  return (
    <div className={`v2-dashboard-layout ${isChatCentered ? 'v2-dashboard-layout--returning' : ''}`}>
      {/* Global V2 Header provided by Layout */}



      {/* 3. Dedicated Chat Real Estate (Scrollable) */}
      <div className="v2-dashboard-chat-container">
        <div ref={scrollContentRef} className="v2-dashboard-chat-scroll-wrapper">
          {isGreetingTyping && (
            <BotMessage id="dashboard-greeting-typing" isLoading={true} skipAnimation={false} />
          )}

          {showGreeting && (
            <BotMessage
              id="dashboard-greeting"
              text={t('v2DashboardGreeting_instant', { name: userInfo.first_name || '' })}
              skipAnimation={messages.length > 0}
            />
          )}

          {loadingRetroText && (
            <BotMessage id="dashboard-retro-typing" isLoading={true} skipAnimation={false} />
          )}

          {showRetroText && renderRetroBubble()}

          {messages.map((msg, index) => {
            if (msg.role === 'assistant') {
              const isCatalog =
                msg.text === 'SHOW_CATALOG' ||
                msg.text === 'SHOW_CATALOG_OFF_TOPIC' ||
                msg.text?.includes('agent-nav-catalog') ||
                msg.text?.includes('SHOW_CATALOG') ||
                msg.text?.includes('SHOW_CATALOG_OFF_TOPIC');
              const isSmartCatalog = msg.text === 'SHOW_SMART_CATALOG';
              const isOffTopic =
                msg.text === 'SHOW_CATALOG_OFF_TOPIC' ||
                msg.text?.includes('SHOW_CATALOG_OFF_TOPIC') ||
                (msg.text ? msg.text.includes(t('v2Catalog_off_topic_intro').substring(0, 19)) : false);

              const cleanText = msg.text === 'COMMUNICATION_ERROR'
                ? t('agentCommunicationError')
                : (msg.text
                    ? msg.text
                        .replace('SHOW_CATALOG_OFF_TOPIC', '')
                        .replace('SHOW_CATALOG', '')
                        .replace('SHOW_SMART_CATALOG', '')
                        .replace('agent-nav-catalog', '')
                        .trim()
                    : '');

              if (isCatalog) {
                return (
                  <BotMessage
                    key={index}
                    id={`bot-msg-${index}`}
                    skipAnimation={true}
                    isError={msg.isError}
                  >
                    <CatalogGrid 
                      variant={isOffTopic ? 'off-topic' : 'select-context'} 
                      introText={cleanText || undefined}
                    />
                  </BotMessage>
                );
              }

              if (isSmartCatalog && msg.data?.smart_catalog_items) {
                return (
                  <BotMessage
                    key={index}
                    id={`bot-msg-${index}`}
                    skipAnimation={true}
                    isError={msg.isError}
                  >
                    <SmartLessonCatalog
                      items={msg.data.smart_catalog_items}
                      introText={cleanText || undefined}
                    />
                  </BotMessage>
                );
              }

              return (
                <BotMessage
                  key={index}
                  id={`bot-msg-${index}`}
                  text={cleanText || undefined}
                  skipAnimation={true}
                  isError={msg.isError}
                />
              );
            } else {
              return (
                <UserMessage
                  key={index}
                  text={msg.text}
                  firstName={userInfo.first_name}
                  lastName={userInfo.last_name}
                />
              );
            }
          })}

          {(isAgentLoading || isAgentLoadingManual) && (
            <BotMessage
              id="typing-indicator"
              isLoading={true}
              skipAnimation={false}
            />
          )}

          {/* Render Quick Reply Chips inline in the stream */}
          {showQuickReplies && !isAgentLoading && !isAgentLoadingManual && (
            <div className="v2-quick-reply-wrapper">
              <QuickReplyChips
                options={quickReplyOptions}
                onSelect={(val) => handleSendMessage(val, true)}
                disabled={isAgentLoading || isAgentLoadingManual}
              />
            </div>
          )}

          {/* Intersection sentinel for auto-scroll */}
          <div ref={chatBottomRef} />
          {/* Physical spacer for breathing room */}
          <div className="v2-dashboard-chat-bottom-spacer" />
        </div>
      </div>

      {/* 4. Bottom Interaction Tool */}
      <div className="v2-dashboard-footer">
        <ChatInputTool
          onSendMessage={handleSendMessage}
          onMicClick={() => console.log('mic')}
          onAttachClick={() => console.log('attach')}
          onStudyModeClick={openModesModal}
        />
      </div>
    </div>
  );
}
